import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Move, Scan, Radio } from 'lucide-react';

const ExitDeathRecordsPage = () => {
    const [expandedItem, setExpandedItem] = useState(null);
    const [distortTitle, setDistortTitle] = useState(false);
    const [radioHover, setRadioHover] = useState(false);

    useEffect(() => {
        const distortInterval = setInterval(() => {
            setDistortTitle(true);
            setTimeout(() => setDistortTitle(false), 150);
        }, 5000);
        return () => clearInterval(distortInterval);
    }, []);

    const recordSections = [
        {
            title: "Sonic Machinery",
            content: "We forge relentless techno tools for peak-time annihilation. Our sound emerges from abused hardware and overloaded signal chains, where distortion becomes an instrument and electrical interference morphs into rhythm.",
            phrases: [
                "Industrial-grade percussion",
                "Feedback manipulation",
                "High-voltage textures"
            ]
        },
        {
            title: "Beijing Crucible",
            content: "Born in the underground spaces of a megacity that never sleeps. Exit Death Records channels Beijing's concrete pulse into uncompromising electronic music that moves bodies and breaks expectations.",
            phrases: [
                "Night energy distilled",
                "Urban rhythm conversion",
                "Underground ethos encoded"
            ]
        },
        {
            title: "Hardware Alchemy",
            content: "Our studio operates as a circuit-bending laboratory. We resurrect obsolete gear, push equipment beyond intended limits, and transform technical failures into distinctive sonic signatures.",
            phrases: [
                "Circuit manipulation",
                "Analog distortion rituals",
                "Voltage-controlled chaos"
            ]
        }
    ];

    return (
        <div className="bg-black min-h-screen w-full font-mono text-white">
            <div className="max-w-6xl mx-auto p-12">
                {/* Header */}
                <div className="mb-24">
                    <Link
                        to="/projects"
                        className="inline-flex items-center space-x-2 text-sm hover:opacity-70 transition-opacity mb-16 text-gray-400"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back to Projects</span>
                    </Link>

                    <div className="relative">
                        <h1 className={`text-7xl font-bold mb-12 ${
                            distortTitle ? 'translate-x-[1px] skew-x-1' : ''
                        }`}>
                            Exit Death Records
                            <div className={`absolute inset-0 text-red-500 opacity-30 blur-sm transition-transform duration-100 ${
                                distortTitle ? '-translate-x-[2px]' : ''
                            }`}>
                                Exit Death Records
                            </div>
                        </h1>
                        <p className="text-2xl text-gray-400 mb-8">
                            Industrial techno forged in Beijing's underground. High-intensity tools for nocturnal warfare.
                        </p>
                    </div>

                    {/* SoundCloud Link */}
                    <div className="absolute top-8 right-8">
                        <a
                            href="https://soundcloud.com/wirzmusic"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-white group relative"
                            onMouseEnter={() => setRadioHover(true)}
                            onMouseLeave={() => setRadioHover(false)}
                        >
                            <Radio className={`w-6 h-6 transition-all duration-500 ${
                                radioHover ? 'rotate-180 scale-110' : ''
                            }`} />
                            <div className={`absolute -bottom-6 right-0 whitespace-nowrap text-xs transition-all duration-300 ${
                                radioHover ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'
                            }`}>
                                Tune in
                            </div>
                        </a>
                    </div>
                </div>

                {/* Content */}
                <div className="space-y-16">
                    {recordSections.map((section, index) => (
                        <div
                            key={index}
                            className="group border-b border-gray-800 pb-16 last:border-0"
                            onMouseEnter={() => setExpandedItem(index)}
                            onMouseLeave={() => setExpandedItem(null)}
                        >
                            <div className="flex items-start space-x-8">
                                <span className="text-sm opacity-50 mt-1">{(index + 1).toString().padStart(2, '0')}</span>
                                <div className="flex-1">
                                    <h3 className="text-3xl mb-4 relative">
                                        <span className="relative inline-block">
                                            {section.title}
                                            <span className={`absolute bottom-0 left-0 h-[2px] bg-red-500 transition-all duration-500 ${
                                                expandedItem === index ? 'w-full' : 'w-0'
                                            }`}></span>
                                        </span>
                                    </h3>
                                    <div className={`transition-all duration-500 ${
                                        expandedItem === index
                                            ? 'opacity-100 max-h-96'
                                            : 'opacity-70 max-h-32 overflow-hidden'
                                    }`}>
                                        <p className="text-xl leading-relaxed mb-6 text-gray-300">
                                            {section.content}
                                        </p>
                                        <div className="flex items-center space-x-4 text-red-400 font-medium">
                                            {section.phrases.map((phrase, i) => (
                                                <React.Fragment key={i}>
                                                    <span className="font-mono text-sm tracking-tight">{phrase}</span>
                                                    {i < section.phrases.length - 1 && (
                                                        <span className="text-gray-600">{'///'}</span>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Concept Section */}
                <div className="border-t border-gray-800 pt-16 mt-16">
                    <div className="text-center space-y-8">
                        <Scan className="w-8 h-8 mx-auto opacity-30 animate-pulse" />
                        <h2 className="text-4xl font-bold">Amanecer</h2>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            We envision a space where the night never surrenders. A concrete sanctuary for extended communion with sound systems, where industrial aesthetics meet functional design for all-night sessions. Amanecer is an afters club in the works, set to open in Barcelona shortly.
                        </p>
                        <div className="inline-block text-white group hover:-translate-y-0.5 transition-transform">
                            <a href="https://www.amanecer.club/" target="_blank" rel="noopener noreferrer" className="text-xl font-bold opacity-50 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                                <span>Visit Amanecer</span>
                                <Move className="w-5 h-5 animate-pulse" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            <footer className="text-center text-gray-500 mt-8 pb-8">
                © Exit Death Records — Singular audio entity operating since 2024
            </footer>
        </div>
    );
};

export default ExitDeathRecordsPage;